import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
    FaArrowLeft, FaCalculator, FaHome, FaBuilding,
    FaHardHat, FaRulerCombined, FaDollarSign, FaChartPie,
    FaChevronDown, FaMapMarkerAlt
} from 'react-icons/fa'
import {
    MARKET_OPTIONS,
    SCOPE_OPTION_TO_PCT,
    ADD_ALT_DIFFICULTY_DEFAULTS,
    useArchConfig,
    useArchDifficultyLevels,
    useArchScopeMultipliers,
    useArchDisciplinePresets,
    useArchMinFloors,
} from '../../lib/archConfig'
import { useArchAddAltDifficulties } from '../../lib/archAddAltDifficulties'
import { useArchCivilDifficultyLevels } from '../../lib/archCivilDifficulties'
import {
    calcNewConstruction,
    calcAddRem,
    calcCivilSplit,
    round2,
} from '../../lib/archQuickCalc'

const fmt = (n) =>
    Number(n || 0).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })

const FADE = { initial: { opacity: 0, y: 8 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0, y: -4 }, transition: { duration: 0.2 } }

// ── Summary Card (reused across tabs) ───────────────────────────────────────
function SummaryCard({ result, t }) {
    if (!result) return null
    const total = Number(result.totalContract) || 0
    const safe = (n) => Number(n) || 0
    const toPct = (amt) => total > 0 ? round2((safe(amt) / total) * 100) : 0
    const rows = [
        { label: t('admin.arch.disc_arch'),   pct: toPct(result.archAmt),   amt: safe(result.archAmt),   color: 'bg-blue-500' },
        { label: t('admin.arch.disc_struct'), pct: toPct(result.structAmt), amt: safe(result.structAmt), color: 'bg-green-500' },
        { label: t('admin.arch.disc_mep'),    pct: toPct(result.mepAmt),    amt: safe(result.mepAmt),    color: 'bg-purple-500' },
        { label: t('admin.arch.disc_civil'),  pct: toPct(result.civilAmt),  amt: safe(result.civilAmt),  color: 'bg-amber-500' },
        { label: t('admin.arch.disc_admin'),  pct: toPct(result.adminAmt),  amt: safe(result.adminAmt),  color: 'bg-gray-500' },
        ...(safe(result.civilAdminAmt) > 0 ? [{ label: t('admin.arch.disc_civil_admin'), pct: toPct(result.civilAdminAmt), amt: safe(result.civilAdminAmt), color: 'bg-indigo-500' }] : []),
    ]

    return (
        <div className="p-5 mt-6 bg-white rounded-2xl border border-gray-200 shadow-sm">
            <div className="flex gap-2 items-center mb-4">
                <FaChartPie className="text-primary" size={16} />
                <h3 className="text-sm font-bold tracking-wide text-gray-800 uppercase">
                    {t('admin.arch.price_summary')}
                </h3>
            </div>

            <div className="flex flex-col justify-center items-center py-4 mb-4 bg-gradient-to-br rounded-xl border from-primary/5 to-primary/10 border-primary/10">
                <span className="mb-1 text-xs font-semibold tracking-wide text-gray-500 uppercase">
                    {t('admin.arch.total_contract')}
                </span>
                <span className="text-3xl font-extrabold tracking-tight text-gray-900">
                    ${fmt(total)}
                </span>
            </div>

            <div className="space-y-2.5">
                {rows.map((r, i) => (
                    <div key={i} className="flex gap-3 items-center">
                        <div className={`w-2 h-2 rounded-full ${r.color}`} />
                        <span className="flex-1 text-sm text-gray-600">{r.label}</span>
                        <span className="w-10 text-xs font-semibold text-right text-gray-400">{r.pct}%</span>
                        <span className="w-24 text-sm font-bold tabular-nums text-right text-gray-800">${fmt(r.amt)}</span>
                    </div>
                ))}
            </div>

            {/* Calculation breakdown for transparency */}
            {result.computedAddAlt != null && (
                <div className="pt-3 mt-4 space-y-1 border-t border-gray-100">
                    <div className="flex justify-between text-[10px] text-gray-400">
                        <span>{t('admin.arch.computed_value')}</span>
                        <span>${fmt(result.computedAddAlt)}</span>
                    </div>
                    {result.activeMin != null && (
                        <div className="flex justify-between text-[10px] text-gray-400">
                            <span>{t('admin.arch.min_floor')}</span>
                            <span>${fmt(result.activeMin)}</span>
                        </div>
                    )}
                    {result.scopeMult != null && (
                        <div className="flex justify-between text-[10px] text-gray-400">
                            <span>{t('admin.arch.scope_multiplier')}</span>
                            <span>×{fmt(result.scopeMult)}</span>
                        </div>
                    )}
                </div>
            )}

            {result.effectiveSqft != null && (
                <div className="flex justify-between items-center pt-3 mt-3 border-t border-gray-100">
                    <span className="text-xs text-gray-400">{t('admin.arch.area')}</span>
                    <span className="text-xs font-bold text-gray-700">
                        {fmt(result.effectiveSqft)} {t('admin.arch.area_sqft')}
                    </span>
                </div>
            )}
        </div>
    )
}

// ── Editable Percentages (reused) ────────────────────────────────────────────
function EditablePercents({ percents, setPercents, scope, setScope, t, hideCivil = false }) {
    const mainKeys = ['archPct', 'structPct', 'mepPct', 'adminPct']
    const civilKeys = ['civilPct', 'civilAdminPct']
    const allKeys = hideCivil ? mainKeys : [...mainKeys, ...civilKeys]

    const [drafts, setDrafts] = useState(() => { const d = {}; allKeys.forEach(k => d[k] = String(percents[k] ?? 0)); return d })
    useEffect(() => { setDrafts(prev => { const n = { ...prev }; allKeys.forEach(k => n[k] = String(percents[k] ?? 0)); return n }) }, [percents, allKeys.join(',')])

    const numericPercents = {}; allKeys.forEach(k => numericPercents[k] = Number(drafts[k]) || 0)
    const mainSum = mainKeys.reduce((s, k) => s + numericPercents[k], 0)
    const civilSum = civilKeys.reduce((s, k) => s + numericPercents[k], 0)
    const mainOk = Math.abs(mainSum - 100) < 0.01
    const civilOk = hideCivil || Math.abs(civilSum - 100) < 0.01
    const isOk = mainOk && civilOk

    const handleChange = (field, value) => {
        setDrafts(prev => ({ ...prev, [field]: value }))
    }

    const normalizeGroup = (keys) => {
        const nums = {}
        keys.forEach(k => { nums[k] = Math.max(0, Math.min(100, Math.round(Number(drafts[k]) || 0))) })
        const total = keys.reduce((s, k) => s + nums[k], 0)
        if (total === 0) {
            const share = Math.floor(100 / keys.length)
            let remainder = 100 - share * keys.length
            keys.forEach((k, i) => { nums[k] = share + (i < remainder ? 1 : 0) })
        } else if (total !== 100) {
            const factor = 100 / total
            let distributed = 0
            keys.forEach(k => {
                nums[k] = Math.max(0, Math.floor(nums[k] * factor))
                distributed += nums[k]
            })
            const drift = 100 - distributed
            if (drift !== 0) {
                const maxKey = keys.reduce((a, b) => nums[a] > nums[b] ? a : b)
                nums[maxKey] += drift
            }
        }
        return nums
    }

    const normalizeTo100 = () => {
        const mainNums = normalizeGroup(mainKeys)
        if (hideCivil) {
            setPercents({ ...mainNums, civilPct: 0, civilAdminPct: 0 })
        } else {
            const civilNums = normalizeGroup(civilKeys)
            setPercents({ ...mainNums, ...civilNums })
        }
    }

    const mainFields = [
        { key: 'archPct', label: t('admin.arch.disc_arch'), color: 'text-blue-500' },
        { key: 'structPct', label: t('admin.arch.disc_struct'), color: 'text-green-500' },
        { key: 'mepPct', label: t('admin.arch.disc_mep'), color: 'text-purple-500' },
        { key: 'adminPct', label: t('admin.arch.disc_admin'), color: 'text-gray-500' },
    ]
    const civilFields = [
        { key: 'civilPct', label: t('admin.arch.disc_civil'), color: 'text-amber-500' },
        { key: 'civilAdminPct', label: t('admin.arch.disc_civil_admin'), color: 'text-indigo-500' },
    ]

    return (
        <div className="space-y-3">
            {/* Main pool percentage inputs */}
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {mainFields.map((f) => (
                    <div key={f.key}>
                        <label className={`block mb-1 text-[11px] font-medium ${f.color}`}>{f.label}</label>
                        <div className="flex gap-1 items-center">
                            <input
                                type="text" inputMode="numeric" pattern="[0-9]*"
                                value={drafts[f.key] ?? '0'}
                                onChange={(e) => handleChange(f.key, e.target.value)}
                                onBlur={normalizeTo100}
                                className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                            />
                            <span className="text-xs text-gray-400">%</span>
                        </div>
                    </div>
                ))}
            </div>
            <p className={`text-xs font-semibold ${mainOk ? 'text-emerald-500' : 'text-amber-500'}`}>
                {mainOk ? t('admin.arch.pct_ok') : t('admin.arch.pct_warn')} — {mainSum}%
            </p>

            {/* Civil pool percentage inputs */}
            {!hideCivil && (
                <>
                    <div className="grid grid-cols-2 gap-3 pt-2 border-t border-gray-100">
                        {civilFields.map((f) => (
                            <div key={f.key}>
                                <label className={`block mb-1 text-[11px] font-medium ${f.color}`}>{f.label}</label>
                                <div className="flex gap-1 items-center">
                                    <input
                                        type="text" inputMode="numeric" pattern="[0-9]*"
                                        value={drafts[f.key] ?? '0'}
                                        onChange={(e) => handleChange(f.key, e.target.value)}
                                        onBlur={normalizeTo100}
                                        className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                                    />
                                    <span className="text-xs text-gray-400">%</span>
                                </div>
                            </div>
                        ))}
                    </div>
                    <p className={`text-xs font-semibold ${civilOk ? 'text-emerald-500' : 'text-amber-500'}`}>
                        {civilOk ? t('admin.arch.pct_ok') : t('admin.arch.pct_warn')} — {civilSum}%
                    </p>
                </>
            )}
        </div>
    )
}

// ═══════════════════════════════════════════════════════════════════════════════
//  MODULE-LEVEL REUSABLE COMPONENTS (must live outside tabs to avoid remounts)
// ═══════════════════════════════════════════════════════════════════════════════

function DiffSelect({ value, onChange, options, label }) {
    return (
        <div className="flex-1 min-w-[6rem]">
            <label className="block mb-1 text-[10px] font-bold text-gray-500 uppercase tracking-wide">{label}</label>
            <div className="relative">
                <select
                    value={value}
                    onChange={(e) => onChange(Number(e.target.value))}
                    className="w-full px-2.5 py-2 pr-7 text-sm rounded-lg border border-gray-200 bg-white text-gray-700 appearance-none focus:outline-none focus:ring-2 focus:ring-primary/30"
                >
                    {options.map((opt) => (
                        <option key={opt.id || opt.value} value={opt.value}>
                            {opt.label}
                        </option>
                    ))}
                </select>
                <FaChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={10} />
            </div>
        </div>
    )
}

function Row({ icon, title, sqftVal, setSqftVal, diffVal, setDiffVal, diffOptions, t }) {
    return (
        <div className="p-3.5 bg-white rounded-xl border border-gray-200 shadow-sm space-y-3">
            <div className="flex gap-2 items-center">
                {icon}
                <span className="text-xs font-bold text-gray-700">{title}</span>
            </div>
            <div className="flex flex-wrap gap-3 items-end">
                <div className="flex-1 min-w-[7rem]">
                    <label className="block mb-1 text-[10px] font-bold text-gray-500 uppercase tracking-wide">{t('admin.arch.area')}</label>
                    <input
                        type="text" inputMode="numeric" pattern="[0-9]*"
                        value={sqftVal}
                        onChange={(e) => setSqftVal(e.target.value)}
                        placeholder="sqft"
                        className="w-full px-2.5 py-2 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/30"
                    />
                </div>
                <DiffSelect value={diffVal} onChange={setDiffVal} options={diffOptions} label={t('admin.arch.difficulty')} />
            </div>
        </div>
    )
}

// ═══════════════════════════════════════════════════════════════════════════════
//  TAB 1 — NEW CONSTRUCTION
// ═══════════════════════════════════════════════════════════════════════════════
function NewConstTab({ t, marketOptions, diffLevels, civilDiffLevels, getMarketConfig, scopeMultipliers, disciplinePresets }) {
    const [market, setMarket] = useState('FL_RES')
    const [sqft, setSqft] = useState('')
    const [difficulty, setDifficulty] = useState(1.0)
    const [scope, setScope] = useState('full')
    const [percents, setPercents] = useState({ ...SCOPE_OPTION_TO_PCT.full, civilAdminPct: 20 })
    const [useCivil, setUseCivil] = useState(false)
    const [civilSqft, setCivilSqft] = useState('')
    const [civilDifficulty, setCivilDifficulty] = useState(1.0)
    const [useSameSqft, setUseSameSqft] = useState(true)
    const [useSameDiff, setUseSameDiff] = useState(true)

    const savedCivilPctRef = useRef(null)
    const prevUseCivilRef = useRef(false)
    useEffect(() => {
        const wasCivil = prevUseCivilRef.current
        if (!wasCivil && useCivil) {
            // Entering civil mode — main pool stays at 100%, civil pool gets 80/20
            const restoredCivil = savedCivilPctRef.current ?? 80
            savedCivilPctRef.current = null
            setPercents(prev => {
                const mainKeys = ['archPct', 'structPct', 'mepPct', 'adminPct']
                const remaining = mainKeys.reduce((s, k) => s + (prev[k] || 0), 0)
                const factor = remaining > 0 ? 100 / remaining : 0
                const next = {}
                let distributed = 0
                for (const k of mainKeys) {
                    next[k] = Math.max(0, Math.round((prev[k] || 0) * factor))
                    distributed += next[k]
                }
                const drift = 100 - distributed
                if (drift !== 0) {
                    const maxKey = mainKeys.reduce((a, b) => next[a] > next[b] ? a : b)
                    next[maxKey] += drift
                }
                return { ...next, civilPct: restoredCivil, civilAdminPct: 100 - restoredCivil }
            })
        } else if (wasCivil && !useCivil) {
            // Exiting civil mode — save civilPct, zero civil pool
            setPercents(prev => {
                const savedCivil = prev.civilPct || 0
                if (savedCivil > 0) savedCivilPctRef.current = savedCivil
                const mainKeys = ['archPct', 'structPct', 'mepPct', 'adminPct']
                const remaining = mainKeys.reduce((s, k) => s + (prev[k] || 0), 0)
                const factor = remaining > 0 ? 100 / remaining : 0
                const next = {}
                let distributed = 0
                for (const k of mainKeys) {
                    next[k] = Math.max(0, Math.round((prev[k] || 0) * factor))
                    distributed += next[k]
                }
                const drift = 100 - distributed
                if (drift !== 0) {
                    const maxKey = mainKeys.reduce((a, b) => next[a] > next[b] ? a : b)
                    next[maxKey] += drift
                }
                return { ...next, civilPct: 0, civilAdminPct: 0 }
            })
        }
        prevUseCivilRef.current = useCivil
    }, [useCivil])

    const result = useMemo(() => {
        const cfgMain = getMarketConfig(market)
        if (!useCivil) {
            return calcNewConstruction({
                market,
                sqft,
                difficulty,
                disciplineScope: scope,
                customBasePrice: cfgMain.base_price_sqft,
                percents,
                scopeMultipliers,
                disciplinePresets,
            })
        }
        const state = market.startsWith('TX') ? 'TX' : 'FL'
        const civilMarket = `${state}_CIVIL`
        const cfgCivil = getMarketConfig(civilMarket)
        return calcCivilSplit({
            mainMarket: market,
            mainSqft: sqft,
            mainDifficulty: difficulty,
            civilSqft: useSameSqft ? sqft : civilSqft,
            civilDifficulty: useSameDiff ? difficulty : civilDifficulty,
            disciplineScope: scope,
            customMainBasePrice: cfgMain.base_price_sqft,
            customCivilBasePrice: cfgCivil.base_price_sqft,
            percents,
            scopeMultipliers,
            disciplinePresets,
        })
    }, [market, sqft, difficulty, scope, percents, useCivil, civilSqft, civilDifficulty, useSameSqft, useSameDiff, getMarketConfig, scopeMultipliers, disciplinePresets])

    return (
        <motion.div {...FADE} className="space-y-5">
            {/* Market */}
            <div>
                <label className="flex items-center gap-1.5 mb-1.5 text-[10px] font-bold text-gray-500 uppercase tracking-wide">
                    <FaMapMarkerAlt size={10} />
                    {t('admin.arch.market')}
                </label>
                <div className="relative">
                    <select
                        value={market}
                        onChange={(e) => setMarket(e.target.value)}
                        className="w-full px-3 py-2.5 pr-8 text-sm rounded-xl border border-gray-200 bg-white text-gray-700 appearance-none focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all"
                    >
                        {marketOptions
                            .filter((m) => !m.key.endsWith('_CIVIL'))
                            .map((m) => (
                                <option key={m.key} value={m.key}>
                                    {t(m.labelKey)}
                                </option>
                            ))}
                    </select>
                    <FaChevronDown className="absolute right-3 top-1/2 text-gray-400 -translate-y-1/2 pointer-events-none" size={12} />
                </div>
            </div>

            {/* Sqft */}
            <div>
                <label className="flex items-center gap-1.5 mb-1.5 text-[10px] font-bold text-gray-500 uppercase tracking-wide">
                    <FaRulerCombined size={10} />
                    {t('admin.arch.area_direct')}
                </label>
                <div className="relative">
                    <input
                        type="text" inputMode="numeric" pattern="[0-9]*"
                        value={sqft}
                        onChange={(e) => setSqft(e.target.value)}
                        placeholder="e.g. 2500"
                        className="w-full px-3 py-2.5 pr-10 text-sm rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-gray-400 font-medium">sqft</span>
                </div>
            </div>

            {/* Difficulty */}
            <div>
                <label className="flex items-center gap-1.5 mb-1.5 text-[10px] font-bold text-gray-500 uppercase tracking-wide">
                    <FaDollarSign size={10} />
                    {t('admin.arch.difficulty')}
                </label>
                <div className="relative">
                    <select
                        value={difficulty}
                        onChange={(e) => setDifficulty(Number(e.target.value))}
                        className="w-full px-3 py-2.5 pr-8 text-sm rounded-xl border border-gray-200 bg-white text-gray-700 appearance-none focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all"
                    >
                        {diffLevels.map((lvl) => (
                            <option key={lvl.value} value={lvl.value}>
                                {lvl.label} — {lvl.desc}
                            </option>
                        ))}
                    </select>
                    <FaChevronDown className="absolute right-3 top-1/2 text-gray-400 -translate-y-1/2 pointer-events-none" size={12} />
                </div>
            </div>

            {/* Civil toggle */}
            <div className="flex overflow-hidden rounded-xl border border-gray-200">
                <button
                    type="button"
                    onClick={() => setUseCivil(false)}
                    className={`flex-1 py-2.5 text-xs font-bold transition-all ${!useCivil ? 'bg-primary text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
                >
                    {t('admin.arch.main_only')}
                </button>
                <button
                    type="button"
                    onClick={() => setUseCivil(true)}
                    className={`flex-1 py-2.5 text-xs font-bold transition-all ${useCivil ? 'bg-primary text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
                >
                    {t('admin.arch.include_civil')}
                </button>
            </div>

            {/* Civil inputs */}
            {useCivil && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="space-y-4">
                    <div className="p-4 space-y-4 bg-white rounded-xl border border-gray-200 shadow-sm">
                        <div className="flex gap-2 items-center text-xs font-bold text-gray-700">
                            <FaHardHat size={13} className="text-amber-500" />
                            {t('admin.arch.civil_engineering')}
                        </div>

                        <label className="flex gap-2 items-center cursor-pointer">
                            <input
                                type="checkbox"
                                checked={useSameSqft}
                                onChange={(e) => setUseSameSqft(e.target.checked)}
                                className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                            />
                            <span className="text-xs text-gray-600">{t('admin.arch.same_sqft')}</span>
                        </label>

                        {!useSameSqft && (
                            <div>
                                <label className="block mb-1 text-[10px] font-bold text-gray-500 uppercase tracking-wide">{t('admin.arch.area')} (sqft)</label>
                                <input
                                    type="text" inputMode="numeric" pattern="[0-9]*"
                                    value={civilSqft}
                                    onChange={(e) => setCivilSqft(e.target.value)}
                                    placeholder="sqft"
                                    className="px-3 py-2 w-full text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/30"
                                />
                            </div>
                        )}

                        <label className="flex gap-2 items-center cursor-pointer">
                            <input
                                type="checkbox"
                                checked={useSameDiff}
                                onChange={(e) => setUseSameDiff(e.target.checked)}
                                className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                            />
                            <span className="text-xs text-gray-600">{t('admin.arch.same_difficulty')}</span>
                        </label>

                        {!useSameDiff && (
                            <div>
                                <label className="block mb-1 text-[10px] font-bold text-gray-500 uppercase tracking-wide">{t('admin.arch.difficulty')}</label>
                                <div className="relative">
                                    <select
                                        value={civilDifficulty}
                                        onChange={(e) => setCivilDifficulty(Number(e.target.value))}
                                        className="px-3 py-2 pr-8 w-full text-sm text-gray-700 bg-white rounded-lg border border-gray-200 appearance-none focus:outline-none focus:ring-2 focus:ring-primary/30"
                                    >
                                        {civilDiffLevels.map((lvl) => (
                                            <option key={lvl.value} value={lvl.value}>
                                                {lvl.label} — {lvl.desc}
                                            </option>
                                        ))}
                                    </select>
                                    <FaChevronDown className="absolute right-3 top-1/2 text-gray-400 -translate-y-1/2 pointer-events-none" size={10} />
                                </div>
                            </div>
                        )}
                    </div>
                </motion.div>
            )}

            {/* Scope */}
            <EditablePercents percents={percents} setPercents={setPercents} scope={scope} setScope={setScope} t={t} hideCivil={!useCivil} />

            {/* Summary */}
            <SummaryCard result={result} t={t} />
        </motion.div>
    )
}

// ═══════════════════════════════════════════════════════════════════════════════
//  TAB 2 — ADD / REM / ALT
// ═══════════════════════════════════════════════════════════════════════════════
function AddRemTab({ t, marketOptions, addAltDiffs, getMarketConfig, minFloors, scopeMultipliers, disciplinePresets }) {
    const [state, setState] = useState('FL')
    const [type, setType] = useState('RES')
    const [existSqft, setExistSqft] = useState('')
    const [existDiff, setExistDiff] = useState(ADD_ALT_DIFFICULTY_DEFAULTS.FL.exist)
    const [remSqft, setRemSqft] = useState('')
    const [remDiff, setRemDiff] = useState(ADD_ALT_DIFFICULTY_DEFAULTS.FL.remodel)
    const [addSqft, setAddSqft] = useState('')
    const [addDiff, setAddDiff] = useState(ADD_ALT_DIFFICULTY_DEFAULTS.FL.adicion)
    const [scope, setScope] = useState('full')
    const [percents, setPercents] = useState({ ...SCOPE_OPTION_TO_PCT.full })

    // Sync defaults when state changes
    const handleStateChange = (s) => {
        setState(s)
        setExistDiff(ADD_ALT_DIFFICULTY_DEFAULTS[s].exist)
        setRemDiff(ADD_ALT_DIFFICULTY_DEFAULTS[s].remodel)
        setAddDiff(ADD_ALT_DIFFICULTY_DEFAULTS[s].adicion)
    }

    const market = `${state}_${type}`

    const existOptions = addAltDiffs.getOptionsForComponent('exist')
    const remOptions = addAltDiffs.getOptionsForComponent('rem')
    const addOptions = addAltDiffs.getOptionsForComponent('add')

    const result = useMemo(() => {
        const cfg = getMarketConfig(market)
        return calcAddRem({
            market,
            existSqft,
            existDiff,
            remSqft,
            remDiff,
            addSqft,
            addDiff,
            disciplineScope: scope,
            customExistRate: cfg.add_alt_exist_base_price,
            customRemRate: cfg.add_alt_rem_base_price,
            customAddRate: cfg.add_alt_add_base_price,
            percents,
            minFloors: minFloors(`${state}_${type}`),
            scopeMultipliers,
            disciplinePresets,
        })
    }, [market, existSqft, existDiff, remSqft, remDiff, addSqft, addDiff, scope, percents, getMarketConfig, minFloors, scopeMultipliers, disciplinePresets])

    {/* Row and DiffSelect defined at module level below */}

    return (
        <motion.div {...FADE} className="space-y-5">
            {/* State + Type toggles */}
            <div className="flex gap-3">
                <div className="flex-1">
                    <label className="block mb-1.5 text-[10px] font-bold text-gray-500 uppercase tracking-wide">{t('admin.arch.market')}</label>
                    <div className="flex overflow-hidden rounded-xl border border-gray-200">
                        {['FL', 'TX'].map((s) => (
                            <button
                                key={s}
                                onClick={() => handleStateChange(s)}
                                className={`flex-1 py-2.5 text-xs font-bold transition-all ${state === s ? 'bg-primary text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
                            >
                                {s === 'FL' ? t('regions.florida') : t('regions.texas')}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="flex-1">
                    <label className="block mb-1.5 text-[10px] font-bold text-gray-500 uppercase tracking-wide">{t('admin.arch.type')}</label>
                    <div className="flex overflow-hidden rounded-xl border border-gray-200">
                        {['RES', 'COM'].map((ty) => (
                            <button
                                key={ty}
                                onClick={() => setType(ty)}
                                className={`flex-1 py-2.5 text-xs font-bold transition-all ${type === ty ? 'bg-primary text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
                            >
                                {ty === 'RES' ? t('admin.arch.residential') : t('admin.arch.commercial')}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Three rows */}
            <div className="space-y-3">
                <Row
                    icon={<FaHome size={13} className="text-blue-500" />}
                    title={t('admin.arch.existing')}
                    sqftVal={existSqft} setSqftVal={setExistSqft}
                    diffVal={existDiff} setDiffVal={setExistDiff}
                    diffOptions={existOptions}
                    t={t}
                />
                <Row
                    icon={<FaBuilding size={13} className="text-orange-500" />}
                    title={t('admin.arch.remodel')}
                    sqftVal={remSqft} setSqftVal={setRemSqft}
                    diffVal={remDiff} setDiffVal={setRemDiff}
                    diffOptions={remOptions}
                    t={t}
                />
                <Row
                    icon={<FaHardHat size={13} className="text-green-500" />}
                    title={t('admin.arch.addition')}
                    sqftVal={addSqft} setSqftVal={setAddSqft}
                    diffVal={addDiff} setDiffVal={setAddDiff}
                    diffOptions={addOptions}
                    t={t}
                />
            </div>

            {/* Scope */}
            <EditablePercents percents={percents} setPercents={setPercents} scope={scope} setScope={setScope} t={t} hideCivil={true} />

            {/* Summary */}
            <SummaryCard result={result} t={t} />
        </motion.div>
    )
}

// ═══════════════════════════════════════════════════════════════════════════════
//  MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
export default function ArchQuickCalculator({ session, onReturn, onLogout }) {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const [activeTab, setActiveTab] = useState('new_const')

    const { marketOptions, getMarketConfig, rows: configRows, loading: configLoading } = useArchConfig()
    const { levels: diffLevels } = useArchDifficultyLevels()
    const { getOptionsForComponent } = useArchAddAltDifficulties()
    const { levels: civilDiffLevels } = useArchCivilDifficultyLevels()
    const { multipliers: dbScopeMultipliers } = useArchScopeMultipliers()
    const { presets: dbDisciplinePresets } = useArchDisciplinePresets()
    const { getMinFloors } = useArchMinFloors(configRows)

    const addAltDiffs = useMemo(() => ({ getOptionsForComponent }), [getOptionsForComponent])

    const tabs = [
        { key: 'new_const', label: t('admin.arch.ptype_res_new'), icon: <FaHome size={14} /> },
        { key: 'add_rem', label: 'Add / Rem', icon: <FaBuilding size={14} /> },
    ]

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="sticky top-0 z-10 bg-white border-b border-gray-200 shadow-sm">
                <div className="flex justify-between items-center px-4 py-3 mx-auto max-w-3xl">
                    <div className="flex gap-3 items-center">
                        <button
                            onClick={() => (onReturn ? onReturn() : navigate('/admin/estimator'))}
                            className="p-2 text-gray-500 rounded-lg transition-colors hover:bg-gray-100"
                            title={t('common.back')}
                        >
                            <FaArrowLeft size={16} />
                        </button>
                        <div className="flex gap-2 items-center">
                            <div className="p-1.5 bg-primary/10 rounded-lg">
                                <FaCalculator className="text-primary" size={18} />
                            </div>
                            <div>
                                <h1 className="text-sm font-extrabold leading-tight text-gray-900">{t('admin.arch.quick_calc')}</h1>
                                <p className="text-[10px] text-gray-400 font-medium">{t('nav.architecture')}</p>
                            </div>
                        </div>
                    </div>
                    {onLogout && (
                        <button
                            onClick={onLogout}
                            className="p-2 text-red-400 rounded-lg transition-colors hover:bg-red-50"
                            title={t('auth.signOut')}
                        >
                            <FaArrowLeft size={16} className="rotate-180" />
                        </button>
                    )}
                </div>
            </div>

            {/* Content */}
            <div className="px-4 py-6 mx-auto max-w-3xl">
                {/* Tabs */}
                <div className="flex overflow-hidden mb-6 bg-white rounded-xl border border-gray-200 shadow-sm">
                    {tabs.map((tab) => (
                        <button
                            key={tab.key}
                            onClick={() => setActiveTab(tab.key)}
                            className={`flex-1 flex items-center justify-center gap-2 py-3 text-xs font-bold transition-all ${
                                activeTab === tab.key
                                    ? 'bg-primary text-white'
                                    : 'text-gray-500 hover:bg-gray-50'
                            }`}
                        >
                            {tab.icon}
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* Tab content */}
                <AnimatePresence mode="wait">
                    {configLoading ? (
                        <div className="flex justify-center items-center py-20">
                            <div className="w-6 h-6 rounded-full border-2 animate-spin border-primary border-t-transparent" />
                        </div>
                    ) : activeTab === 'new_const' && (
                        <NewConstTab
                            key="new_const"
                            t={t}
                            marketOptions={marketOptions}
                            diffLevels={diffLevels}
                            civilDiffLevels={civilDiffLevels}
                            getMarketConfig={getMarketConfig}
                            scopeMultipliers={dbScopeMultipliers}
                            disciplinePresets={dbDisciplinePresets}
                        />
                    )}
                    {activeTab === 'add_rem' && (
                        <AddRemTab
                            key="add_rem"
                            t={t}
                            marketOptions={marketOptions}
                            addAltDiffs={addAltDiffs}
                            getMarketConfig={getMarketConfig}
                            minFloors={getMinFloors}
                            scopeMultipliers={dbScopeMultipliers}
                            disciplinePresets={dbDisciplinePresets}
                        />
                    )}
                </AnimatePresence>
            </div>
        </div>
    )
}
